import { useEffect, useId, useRef, useState } from 'react';
import { CalendarIcon } from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { formatDayRange } from 'helpers/import-dates';
import { classnames } from 'helpers/utils';
import { useClickOutside } from 'hooks/click-outside';
import styles from './album-header.module.scss';

type DayRange = { startDate: string; endDate: string };

interface AlbumDateRangeProps {
  startDate: string | null;
  endDate: string | null;
  /** Rejects when the change didn't save, which keeps the editor open. */
  onSave: (range: DayRange) => Promise<void>;
  /** Open state, when the parent opens the editor too (the album's … menu). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Shown, not changed (people whose role can't edit the album).
  readOnly?: boolean;
}

// Dates arrive as days ("2019-06-03"); drop any time part to be safe.
const dayOf = (value: string | null) => (value ? value.slice(0, 10) : null);

/**
 * The album's date range, on its own row under the title, e.g. "Jun 3 –
 * Jun 5, 2019". Clicking it opens a From/To editor. An album with only one
 * date reads as that single day. The range is the album's own, any period:
 * its memories' dates don't change it, and it doesn't change theirs.
 */
export const AlbumDateRange = ({
  startDate,
  endDate,
  onSave,
  open: openProp,
  onOpenChange,
  readOnly = false,
}: AlbumDateRangeProps) => {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (value: boolean) => {
    setOpenState(value);
    onOpenChange?.(value);
  };
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const fieldId = useId();
  useClickOutside(wrapRef, () => {
    if (open) setOpen(false);
  });

  const from = dayOf(startDate) ?? dayOf(endDate);
  const to = dayOf(endDate) ?? dayOf(startDate);
  const label = from ? formatDayRange(from, to) : '';

  // Each time the editor opens (here or from the menu), start from the saved
  // range.
  useEffect(() => {
    if (!open) return;
    setStart(from ?? '');
    setEnd(to ?? '');
  }, [open]);

  const backwards = !!start && !!end && end < start;
  const invalid = !start || !end || backwards;

  const save = async () => {
    if (invalid) return;
    if (start === from && end === to) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onSave({ startDate: start, endDate: end });
      setOpen(false);
    } catch {
      // The header already told the user; leave the editor open to retry.
    } finally {
      setSaving(false);
    }
  };

  if (readOnly) {
    return label ? (
      <div className={styles.dates}>
        <span className={styles.datesButton}>
          <CalendarIcon />
          {label}
        </span>
      </div>
    ) : null;
  }

  return (
    <div ref={wrapRef} className={styles.dates}>
      <button
        type="button"
        className={classnames(styles.datesButton, {
          [styles.datesButtonOpen]: open,
        })}
        onClick={() => setOpen(!open)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Change the album's dates"
      >
        <CalendarIcon />
        {label || 'Add dates'}
      </button>
      {open && (
        <div className={styles.datesPopover} role="dialog" aria-label="Dates">
          <span className={styles.datesTitle}>Album dates</span>
          <p className={styles.datesText}>
            The period this album covers. It places the album on the timeline.
          </p>
          <div className={styles.datesFields}>
            <label className={styles.datesField} htmlFor={`${fieldId}-from`}>
              From
              <input
                id={`${fieldId}-from`}
                type="date"
                value={start}
                max={end || undefined}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
            <label className={styles.datesField} htmlFor={`${fieldId}-to`}>
              To
              <input
                id={`${fieldId}-to`}
                type="date"
                value={end}
                min={start || undefined}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
          </div>
          {backwards && (
            <span className={styles.datesError}>
              The end date is before the start date.
            </span>
          )}
          <div className={styles.datesActions}>
            <Button
              buttonStyle={ButtonStyle.SecondaryGray}
              buttonSize={ButtonSize.Small}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              buttonStyle={ButtonStyle.Primary}
              buttonSize={ButtonSize.Small}
              disabled={invalid || saving}
              onClick={save}
            >
              Save dates
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
